import Message from 'src/app/models/Message'
import { UnifiedMessageRequest } from '../messageApiClient'

// Shopee-specific supported message types
export const SHOPEE_SUPPORTED_TYPES = ['text', 'image', 'sticker'] as const
export type ShopeeMessageType = typeof SHOPEE_SUPPORTED_TYPES[number]

/**
 * ShopeeMessageHelper
 *
 * Platform-specific helper for Shopee messages.
 * Handles validation and request formatting specific to Shopee.
 */
export class ShopeeMessageHelper {
  /**
   * Validate message type for Shopee platform
   * Shopee only supports: text, image, sticker
   */
  static validateMessageType(messageType: string): boolean {
    return SHOPEE_SUPPORTED_TYPES.includes(messageType as any)
  }

  /**
   * Get human-readable error for unsupported types
   */
  static getUnsupportedTypeError(messageType: string): string {
    return (
      `Message type '${messageType}' is not supported for Shopee. ` +
      `Supported types: ${SHOPEE_SUPPORTED_TYPES.join(', ')}`
    )
  }

  /**
   * Build Shopee-specific request body
   * Formats message data according to Shopee requirements
   */
  static buildRequestBody(
    buyerId: string,
    message: Message
  ): UnifiedMessageRequest {
    const requestBody: UnifiedMessageRequest = {
      messaging_product: 'shopee',
      to: buyerId,
      type: message.messageType!,
    }

    const textContent = typeof message.textContent === 'string'
      ? message.textContent
      : JSON.stringify(message.textContent).replace(/"/g, '')

    // Add content based on message type
    switch (message.messageType) {
      case 'text':
        requestBody.text = {
          body: message.textContent,  // Changed from "text" to "body" to match API spec
          preview_url: message.previewurl ?? false,
        }
        break

      case 'image':
        if (!message.mediaUrl) {
          throw new Error('Image URL is required for image messages')
        }
        requestBody.image = {
          link: message.mediaUrl,
        }
        // Add caption if text content exists (Shopee supports captions)
        if (textContent) {
          (requestBody.image as any).caption = textContent
        }
        break

      case 'sticker':
        if (!message.mediaUrl) {
          throw new Error('Sticker URL is required for sticker messages')
        }
        requestBody.sticker = {
          link: message.mediaUrl,
        }
        break

      default:
        throw new Error(this.getUnsupportedTypeError(message.messageType!))
    }

    return requestBody
  }

  /**
   * Validate all Shopee message requirements
   * Throws error if validation fails
   */
  static validate(message: Message, buyerId: string): void {
    // Validate buyer ID
    if (!buyerId || buyerId.trim() === '') {
      throw new Error('Buyer ID is required for Shopee messages')
    }

    // Validate message type
    if (!message.messageType) {
      throw new Error('Message type is required')
    }

    if (!this.validateMessageType(message.messageType)) {
      throw new Error(this.getUnsupportedTypeError(message.messageType))
    }

    // Validate content based on type
    if (message.messageType === 'text' && !message.textContent) {
      throw new Error('Text content is required for text messages')
    }

    if (['image', 'sticker'].includes(message.messageType) && !message.mediaUrl) {
      throw new Error(`Media URL is required for ${message.messageType} messages`)
    }
  }
}
